import { useEffect, useRef } from 'react';

const highlights = [
  { icon: 'ri-award-line', title: '전문 기술력', desc: '25년 이상의 제조 경험' },
  { icon: 'ri-settings-3-line', title: '맞춤 설계', desc: '고객 사양에 최적화' },
  { icon: 'ri-shield-check-line', title: '품질 보증', desc: '엄격한 품질 관리 시스템' },
  { icon: 'ri-customer-service-2-line', title: '사후 관리', desc: '신속한 A/S 서비스' },
];

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="bg-[#F8F6F2] py-28" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          className="fade-up mb-16"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <span className="inline-block text-xs font-bold tracking-widest text-[#C0392B] border border-[#C0392B]/30 px-3 py-1 rounded-full mb-4">
            ABOUT US
          </span>
          <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] leading-tight">
            기술과 혁신으로<br />
            <span className="text-[#C0392B]">산업의 미래</span>를 만듭니다
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Text */}
          <div>
            <div
              className="fade-up"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s' }}
            >
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                <strong className="text-[#1C1C1C]">(주)에스제이엔지니어링</strong>은 산업기계설비 및 도장설비 제조 분야의 전문기업으로,
                2000년 창업 이래 25년 이상 축적된 탁월한 기술력을 바탕으로 다양한 산업 현장에 최적화된 설비를 공급해 왔습니다.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                자동차, 조선, 전자, 철강 등 주요 산업 분야의 생산 라인에 맞춤형 산업기계설비와
                최첨단 도장설비를 설계·제작·납품하며, 고객의 생산성 향상과 품질 개선에 기여하고 있습니다.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                대표이사를 중심으로 전문 엔지니어들이 고객 맞춤형 솔루션 개발에 매진하며,
                지속적인 R&D 투자를 통해 기술 혁신을 선도하고 있습니다.
              </p>

              {/* Company Info */}
              <div className="border-l-4 border-[#C0392B] pl-5 space-y-2">
                <p className="text-sm text-gray-500"><span className="font-semibold text-[#1C1C1C]">소재지</span> &nbsp; 경기도 안산시 단원구 엠티브이10로10번길9</p>
                <p className="text-sm text-gray-500"><span className="font-semibold text-[#1C1C1C]">사업자번호</span> &nbsp; 140-81-90658</p>
              </div>
            </div>
          </div>

          {/* Right: Images + Stats */}
          <div>
            <div
              className="fade-up relative"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s' }}
            >
              {/* Stats */}
              <div className="flex gap-10 mb-10">
                {[
                  { num: '30+', label: 'Years\nExperience' },
                  { num: '500+', label: 'Projects\nCompleted' },
                  { num: '50+', label: 'Expert\nEngineers' },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-5xl font-black text-[#C0392B] leading-none">{s.num}</p>
                    <p className="text-xs text-gray-400 mt-1 whitespace-pre-line leading-relaxed">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Stacked Images */}
              <div className="relative h-[360px]">
                <div className="absolute top-0 left-0 w-[58%] h-[260px] rounded-2xl overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/30361745e7b180d534cf1d7b4cddbd16.jpg"
                    alt="공장 내부"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute bottom-0 right-0 w-[48%] h-[240px] rounded-2xl overflow-hidden border-4 border-white">
                  <img
                    src="https://readdy.ai/api/search-image?query=industrial%20painting%20equipment%20spray%20booth%20coating%20line%20with%20professional%20equipment%2C%20automated%20painting%20system%2C%20clean%20industrial%20environment%2C%20steel%20structures%2C%20modern%20coating%20facility%2C%20detailed%20technical%20machinery&width=400&height=300&seq=about002&orientation=portrait"
                    alt="도장설비 현장"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {/* Decorative Element */}
                <div className="absolute top-4 left-4 w-16 h-16 rounded-xl bg-[#C0392B]/10 -z-10 translate-x-[-8px] translate-y-[-8px]" />
              </div>
            </div>
          </div>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
          {highlights.map((item, i) => (
            <div
              key={item.title}
              className="fade-up bg-white rounded-2xl p-6 flex flex-col gap-3"
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                transition: `opacity 0.7s ease ${0.3 + i * 0.1}s, transform 0.7s ease ${0.3 + i * 0.1}s`,
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C0392B]/10">
                <i className={`${item.icon} text-xl text-[#C0392B]`} />
              </div>
              <p className="font-bold text-[#1C1C1C] text-sm">{item.title}</p>
              <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
